(() => {
  if (customElements.get('event-drawer')) return;

  class EventDrawer extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.dialog = this.querySelector('.event-drawer__dialog');
      if (!this.dialog) return;

      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.dialog.inert = true;
      this.dialog.setAttribute('aria-hidden', 'true');

      document.addEventListener('click', (event) => this.handleClick(event), options);
      document.addEventListener('keydown', (event) => this.handleKeydown(event), { ...options, capture: true });
      document.addEventListener('shopify:section:select', (event) => {
        if (this.belongsToSection(event)) this.open();
      }, options);
      document.addEventListener('shopify:section:deselect', (event) => {
        if (this.belongsToSection(event)) this.close();
      }, options);
      document.addEventListener('shopify:section:unload', (event) => {
        if (this.belongsToSection(event)) this.disconnectedCallback();
      }, options);

      if (window.location.hash === '#event-drawer') this.open();
    }

    disconnectedCallback() {
      this.close(false);
      this.controller?.abort();
      this.controller = null;
      clearTimeout(this.copyTimeout);
    }

    belongsToSection(event) {
      return event.detail?.sectionId === this.dataset.sectionId ||
        (event.target instanceof Element && event.target.contains(this));
    }

    handleClick(event) {
      if (!(event.target instanceof Element)) return;
      const target = event.target;
      const anchor = target.closest('a[href]');

      if (anchor && ['event-drawer', '#event-drawer'].includes(anchor.getAttribute('href'))) {
        event.preventDefault();
        this.open(anchor);
        return;
      }

      if (!this.contains(target)) {
        this.closeCalendar();
        return;
      }

      if (target.closest('.event-drawer__close, [data-event-drawer-overlay]')) {
        event.preventDefault();
        this.close();
        return;
      }

      const copyButton = target.closest('.event-drawer__copy-link');
      if (copyButton) {
        event.preventDefault();
        this.copyLink(copyButton);
        this.closeCalendar();
        return;
      }

      const calendarTrigger = target.closest('.event-drawer-atc-trigger');
      if (calendarTrigger) {
        event.preventDefault();
        const dropdown = this.querySelector('.event-drawer-atc-dropdown');
        if (!dropdown) return;
        const shouldOpen = dropdown.hidden;
        this.closeCalendar();
        dropdown.hidden = !shouldOpen;
        calendarTrigger.setAttribute('aria-expanded', String(shouldOpen));
        return;
      }

      const calendarOption = target.closest('.event-drawer-atc-option');
      if (calendarOption) {
        event.preventDefault();
        this.addToCalendar(calendarOption.dataset.provider);
      }
      this.closeCalendar();
    }

    open(opener) {
      if (this.hasAttribute('data-open')) return;
      this.opener = opener || document.activeElement;
      this.previousOverflow = document.body.style.getPropertyValue('overflow');
      this.previousOverflowPriority = document.body.style.getPropertyPriority('overflow');
      document.body.style.setProperty('overflow', 'hidden');
      this.dialog.inert = false;
      this.dialog.setAttribute('aria-hidden', 'false');
      this.setAttribute('data-open', '');
      (this.querySelector('.event-drawer__close') || this.dialog).focus({ preventScroll: true });
    }

    close(restoreFocus = true) {
      if (!this.hasAttribute('data-open')) return;
      this.closeCalendar();
      this.removeAttribute('data-open');
      this.dialog.inert = true;
      this.dialog.setAttribute('aria-hidden', 'true');
      if (this.previousOverflow) {
        document.body.style.setProperty('overflow', this.previousOverflow, this.previousOverflowPriority);
      } else {
        document.body.style.removeProperty('overflow');
      }
      if (restoreFocus && this.opener?.isConnected) this.opener.focus({ preventScroll: true });
      this.opener = null;
    }

    handleKeydown(event) {
      if (!this.hasAttribute('data-open')) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        const dropdown = this.querySelector('.event-drawer-atc-dropdown');
        if (dropdown && !dropdown.hidden) {
          this.closeCalendar();
          this.querySelector('.event-drawer-atc-trigger')?.focus();
        } else {
          this.close();
        }
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = [...this.dialog.querySelectorAll(
        'a[href], button, input, select, textarea, [tabindex]'
      )].filter((element) => !element.disabled && element.tabIndex >= 0 && element.getClientRects().length);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (!first) {
        event.preventDefault();
        this.dialog.focus();
      } else if (event.shiftKey && (active === first || !this.dialog.contains(active) || active === this.dialog)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !this.dialog.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    }

    closeCalendar() {
      const dropdown = this.querySelector('.event-drawer-atc-dropdown');
      if (dropdown) dropdown.hidden = true;
      this.querySelector('.event-drawer-atc-trigger')?.setAttribute('aria-expanded', 'false');
    }

    async copyLink(button) {
      const link = button.dataset.link || window.location.href;
      const label = button.querySelector('.events-drawer__text');
      const originalLabel = label?.dataset.originalLabel || label?.textContent;
      if (label) label.dataset.originalLabel = originalLabel;
      let copied = false;

      try {
        await navigator.clipboard.writeText(link);
        copied = true;
      } catch (error) {
        const input = document.createElement('textarea');
        input.value = link;
        input.setAttribute('aria-label', 'Event link');
        input.style.cssText = 'position:fixed;opacity:0;pointer-events:none;';
        this.dialog.appendChild(input);
        input.select();
        try {
          copied = document.execCommand('copy');
        } catch (copyError) {
          copied = false;
        }
        input.remove();
        if (this.hasAttribute('data-open')) button.focus({ preventScroll: true });
      }

      if (!label || !this.isConnected) return;
      label.textContent = copied ? 'Copied!' : 'Copy failed';
      clearTimeout(this.copyTimeout);
      this.copyTimeout = setTimeout(() => { label.textContent = originalLabel; }, 2000);
    }

    addToCalendar(provider) {
      const trigger = this.querySelector('.event-drawer-atc-trigger');
      if (!trigger) return;
      const start = parseLocalDateTime(trigger.dataset.start);
      const end = parseLocalDateTime(trigger.dataset.end);
      if (!start || !end || end <= start) return;

      const details = {
        title: trigger.dataset.title || '',
        description: trigger.dataset.description || '',
        location: trigger.dataset.location || ''
      };
      const days = Math.round((
        Date.UTC(end.getFullYear(), end.getMonth(), end.getDate()) -
        Date.UTC(start.getFullYear(), start.getMonth(), start.getDate())
      ) / 86400000) + 1;
      const firstEnd = new Date(start.getFullYear(), start.getMonth(), start.getDate(),
        end.getHours(), end.getMinutes(), end.getSeconds());

      if (provider === 'google') {
        const params = new URLSearchParams({
          text: details.title,
          details: details.description,
          location: details.location,
          dates: `${formatDate(start)}/${formatDate(firstEnd)}`
        });
        const recurrence = days > 1 ? `&recur=RRULE:FREQ%3DDAILY;COUNT%3D${days}` : '';
        window.open(`https://calendar.google.com/calendar/render?action=TEMPLATE&${params}${recurrence}`, '_blank', 'noopener');
      } else if (provider === 'apple' || provider === 'outlook') {
        const lines = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Alara//Events//EN', 'CALSCALE:GREGORIAN', 'METHOD:PUBLISH'];
        const stamp = new Date().toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');

        // Match the events page: multi-day events have a separate entry each day.
        for (let day = 0; day < days; day++) {
          const dayStart = new Date(start.getFullYear(), start.getMonth(), start.getDate() + day,
            start.getHours(), start.getMinutes(), start.getSeconds());
          const dayEnd = new Date(start.getFullYear(), start.getMonth(), start.getDate() + day,
            end.getHours(), end.getMinutes(), end.getSeconds());
          lines.push(
            'BEGIN:VEVENT',
            `UID:${Date.now()}-${day}-${Math.random().toString(36).slice(2)}@alarajewelry.com`,
            `DTSTAMP:${stamp}`,
            `DTSTART:${formatDate(dayStart)}`,
            `DTEND:${formatDate(dayEnd)}`,
            `SUMMARY:${escapeICS(details.title)}`,
            `DESCRIPTION:${escapeICS(details.description)}`,
            `LOCATION:${escapeICS(details.location)}`,
            'END:VEVENT'
          );
        }
        lines.push('END:VCALENDAR');

        const blob = new Blob([lines.join('\r\n') + '\r\n'], { type: 'text/calendar;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = provider === 'outlook' ? 'event-outlook.ics' : 'event.ics';
        this.dialog.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }
    }
  }

  function parseLocalDateTime(value) {
    const match = (value || '').replace(/Z$/, '').split('.')[0].match(
      /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/
    );
    if (!match) return null;
    const parts = match.slice(1).map((part) => Number(part || 0));
    const date = new Date(parts[0], parts[1] - 1, parts[2], parts[3], parts[4], parts[5]);
    if (date.getFullYear() !== parts[0] || date.getMonth() !== parts[1] - 1 ||
      date.getDate() !== parts[2] || date.getHours() !== parts[3] ||
      date.getMinutes() !== parts[4] || date.getSeconds() !== parts[5]) return null;
    return date;
  }

  function formatDate(date) {
    const pad = (value) => String(value).padStart(2, '0');
    return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}` +
      `T${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`;
  }

  function escapeICS(value) {
    return value.replace(/\\/g, '\\\\').replace(/\r?\n/g, '\\n').replace(/;/g, '\\;').replace(/,/g, '\\,');
  }

  customElements.define('event-drawer', EventDrawer);
})();
